import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  Box,
  Button,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useTimelineWheelZoom,
  useTimelineZoom,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback, useMemo, useRef, useState } from "react";
import { OriginalEventWrapper } from "../hooks/useVideoTimelineData";
import { EventTimelineTrack } from "../components/EventTimelineTrack";
import { LabellerTimelineRow } from "../components/LabellerTimelineRow";
import { LabellerPeriodWithExpandedEvents } from "../hooks/useTimelinesByLabeller";
import { LabellerDetails } from "src/store/api/generatedApi";
import {
  COARSE_TIME_LABEL_FORMAT,
  PeriodBoundaryMarker,
  resolvePeriodBoundary,
} from "./resolvePeriodBoundary";
import { TimelineZoomCaption } from "./TimelineZoomCaption";
import { TimeAxisLayout } from "./timeAxisLayout";

export type { PeriodBoundaryMarker } from "./resolvePeriodBoundary";

dayjs.extend(utc);

const MIN_EVENT_DURATION_SECONDS = 0.5;

// The shaded region and time label for a period boundary falling inside the
// displayed window: "before" shades from the track's own left edge up to the
// boundary, "after" shades from the boundary to the track's right edge.
const PeriodBoundaryOverlay = ({
  side,
  percent,
  label,
}: {
  side: "before" | "after";
  percent: number;
  label: string | undefined;
}) => (
  <>
    <Box
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        bgcolor: "grey.300",
        opacity: 0.4,
        pointerEvents: "none",
        zIndex: 0,
        borderColor: "grey.500",
        ...(side === "before"
          ? { left: 0, width: `${percent}%`, borderRight: "1px dashed" }
          : { left: `${percent}%`, right: 0, borderLeft: "1px dashed" }),
      }}
    />
    {label ? (
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          left: `${percent}%`,
          top: "100%",
          mt: 0.25,
          color: "text.secondary",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          ...(side === "before" ? { transform: "translateX(-100%)" } : {}),
        }}
      >
        {label}
      </Typography>
    ) : null}
  </>
);

interface EditEventTimelinesProps {
  mergedEvents: ScrapEyesEvent[];
  ghostEvents?: ScrapEyesEvent[];
  onGhostEventClick?: (eventId: string) => void;
  // Restricts which ghosts invoke onGhostEventClick and show a clickable
  // cursor/hover — omitted, every ghost does.
  clickableGhostEventIds?: ReadonlySet<string>;
  highlightColorsByEventId?: ReadonlyMap<string, string | null>;
  markMergedEventSelected?: boolean;
  // Height of the pairing connector drawn above each event bar; defaults to
  // EventTimelineTrack's own general-purpose default.
  overbarHeight?: number;
  beforePeriodBoundary?: PeriodBoundaryMarker;
  afterPeriodBoundary?: PeriodBoundaryMarker;
  // Bounds a timing-edge drag to a region narrower than the video window,
  // for a caller whose window can show more than is safe to write into.
  // Omitted, a drag can reach either edge of the video window.
  minTimingBound?: Dayjs;
  maxTimingBound?: Dayjs;
  alignedTimeAxis?: TimeAxisLayout | null;
  originalEventsByLabeller: {
    labellerDetails: LabellerDetails;
    events: OriginalEventWrapper[];
  }[];
  videoStart: Dayjs;
  videoDuration: number;
  currentTime: number;
  onSeek: (time: number) => void;
  onTimingChange?: (updates: { start?: Dayjs; end?: Dayjs }) => void;
  onPickTiming?: (event: ScrapEyesEvent) => void;
  onPickArea?: (event: ScrapEyesEvent) => void;
  onPickMaterial?: (event: ScrapEyesEvent) => void;
  // Must stay opt-in: Manual Review's edit modal renders this component
  // inside its own zoom context already, which it must not pick up.
  enableZoom?: boolean;
}

export const EditEventTimelines = ({
  mergedEvents,
  ghostEvents = [],
  onGhostEventClick,
  clickableGhostEventIds,
  highlightColorsByEventId,
  markMergedEventSelected = false,
  overbarHeight,
  beforePeriodBoundary,
  afterPeriodBoundary,
  minTimingBound,
  maxTimingBound,
  alignedTimeAxis,
  originalEventsByLabeller,
  videoStart,
  videoDuration,
  currentTime,
  onSeek,
  onTimingChange,
  onPickTiming,
  onPickArea,
  onPickMaterial,
  enableZoom = false,
}: EditEventTimelinesProps) => {
  const { t } = useTenantTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomAreaRef = useRef<HTMLDivElement>(null);

  // True during a drag/seek, which captures the window at mousedown and
  // must commit against that same window — so zoom is disabled meanwhile.
  const [pointerInteraction, setPointerInteraction] = useState(false);

  const ambientZoomContext = useTimelineZoom();
  const zoomContext =
    enableZoom && !alignedTimeAxis ? ambientZoomContext : null;

  const fullWindowEnd = useMemo(
    () => videoStart.add(videoDuration, "second"),
    [videoStart, videoDuration]
  );
  const visibleStart =
    alignedTimeAxis?.start ?? zoomContext?.visibleStart ?? videoStart;
  const visibleEnd =
    alignedTimeAxis?.end ?? zoomContext?.visibleEnd ?? fullWindowEnd;
  const visibleDuration = alignedTimeAxis
    ? visibleEnd.diff(visibleStart, "second", true)
    : (zoomContext?.duration ?? videoDuration);
  const trackInsetSx = alignedTimeAxis
    ? {
        ml: `${alignedTimeAxis.leftInsetPx}px`,
        mr: `${alignedTimeAxis.rightInsetPx}px`,
      }
    : undefined;

  const visibleStartSeconds = visibleStart.diff(videoStart, "second", true);
  const visibleCurrentTime = currentTime - visibleStartSeconds;
  const playheadInWindow =
    visibleCurrentTime >= 0 && visibleCurrentTime <= visibleDuration;

  const percentOfWindow = (time: Dayjs) =>
    (time.diff(visibleStart, "second", true) / visibleDuration) * 100;
  const zoomCaptionTimeFormat =
    visibleDuration < 30 ? "HH:mm:ss.SSS" : COARSE_TIME_LABEL_FORMAT;

  const {
    onMouseMove: handleZoomMouseMove,
    onMouseLeave: handleZoomMouseLeave,
  } = useTimelineWheelZoom(
    zoomAreaRef,
    videoStart,
    zoomContext !== null && !pointerInteraction
  );

  const handleSeek = useCallback(
    (clientX: number) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const fraction = Math.max(0, Math.min(1, x / rect.width));
      const seekSeconds = visibleStartSeconds + fraction * visibleDuration;
      onSeek(Math.max(0, Math.min(videoDuration, seekSeconds)));
    },
    [visibleStartSeconds, visibleDuration, videoDuration, onSeek]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setPointerInteraction(true);
      handleSeek(e.clientX);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        handleSeek(moveEvent.clientX);
      };

      const handleMouseUp = () => {
        setPointerInteraction(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [handleSeek]
  );

  const mergedEvent = mergedEvents[0] ?? null;

  const [dragState, setDragState] = useState<{
    edge: "start" | "end";
    percent: number;
  } | null>(null);

  const beforeBoundary = resolvePeriodBoundary({
    marker: beforePeriodBoundary,
    side: "before",
    visibleStart,
    visibleDuration,
  });
  const afterBoundary = resolvePeriodBoundary({
    marker: afterPeriodBoundary,
    side: "after",
    visibleStart,
    visibleDuration,
  });

  const startPercent = mergedEvent ? percentOfWindow(mergedEvent.start) : 0;
  const footageStartPercent = Math.max(0, percentOfWindow(videoStart));
  const footageEndPercent = Math.min(100, percentOfWindow(fullWindowEnd));
  const endPercent = mergedEvent ? percentOfWindow(mergedEvent.end) : 0;

  // Expressed as a percent of the visible window so the wall-clock minimum
  // drag width stays constant in real seconds regardless of zoom.
  const minGapPercent = (MIN_EVENT_DURATION_SECONDS / visibleDuration) * 100;

  const clampFraction = (fraction: number) =>
    Math.max(0, Math.min(1, fraction));
  const lowerTimingBound = minTimingBound?.isAfter(videoStart)
    ? minTimingBound
    : videoStart;
  const upperTimingBound = maxTimingBound?.isBefore(fullWindowEnd)
    ? maxTimingBound
    : fullWindowEnd;
  const minTimingFraction = clampFraction(
    lowerTimingBound.diff(visibleStart, "second", true) / visibleDuration
  );
  const maxTimingFraction = clampFraction(
    upperTimingBound.diff(visibleStart, "second", true) / visibleDuration
  );
  // Zoom/pan toward a neighbouring period's ghosts can move the whole
  // visible window outside [minTimingBound, maxTimingBound].
  const timingDraggable = minTimingFraction < maxTimingFraction;

  const handleEdgeMouseDown = useCallback(
    (edge: "start" | "end") => (e: React.MouseEvent) => {
      if (!containerRef.current || !mergedEvent || !onTimingChange) return;
      e.preventDefault();
      e.stopPropagation();

      setPointerInteraction(true);
      const oppositePercent = edge === "start" ? endPercent : startPercent;
      let lastValidPercent: number | null = null;

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const percent =
          Math.max(
            minTimingFraction,
            Math.min(
              maxTimingFraction,
              (moveEvent.clientX - rect.left) / rect.width
            )
          ) * 100;

        if (
          edge === "start"
            ? percent < oppositePercent - minGapPercent
            : percent > oppositePercent + minGapPercent
        ) {
          lastValidPercent = percent;
          setDragState({ edge, percent });
        }
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        setPointerInteraction(false);

        if (lastValidPercent !== null) {
          const fraction = lastValidPercent / 100;
          const newTime = visibleStart.add(
            fraction * visibleDuration,
            "second"
          );
          if (edge === "start") {
            onTimingChange({ start: newTime });
          } else {
            onTimingChange({ end: newTime });
          }
        }

        setDragState(null);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [
      mergedEvent,
      onTimingChange,
      visibleStart,
      visibleDuration,
      startPercent,
      endPercent,
      minGapPercent,
      minTimingFraction,
      maxTimingFraction,
    ]
  );

  const resolvedStartPercent =
    dragState?.edge === "start" ? dragState.percent : startPercent;
  const resolvedEndPercent =
    dragState?.edge === "end" ? dragState.percent : endPercent;
  const previewLeftPercent = Math.max(0, resolvedStartPercent);
  const previewRightPercent = Math.min(100, resolvedEndPercent);

  // A handle off the visible window isn't a reachable drag target.
  const draggableEdges = timingDraggable
    ? (["start", "end"] as const).filter((edge) => {
        const percent =
          edge === "start" ? resolvedStartPercent : resolvedEndPercent;
        return percent >= 0 && percent <= 100;
      })
    : [];

  // Dozens of neighbour-preview ghosts can be in play; this bounds re-renders
  // on zoom and keeps off-screen ghosts from staying clickable.
  const visibleGhostEvents = useMemo(
    () =>
      ghostEvents.filter(
        (event) =>
          event.end.isAfter(visibleStart) && event.start.isBefore(visibleEnd)
      ),
    [ghostEvents, visibleStart, visibleEnd]
  );

  // Full (unzoomed) window on purpose — downstream rendering clips to the
  // visible window itself, so this stays off the zoom-changing deps.
  const labellerPeriods = useMemo(() => {
    return originalEventsByLabeller.map(
      ({ labellerDetails, events }, index) => {
        if (events.length === 0) {
          return {
            labellerDetails,
            periods: [] as LabellerPeriodWithExpandedEvents[],
            firstEvent: null,
          };
        }

        const sortedEvents = [...events].sort((a, b) =>
          a.scrapEyesEvent.start.diff(b.scrapEyesEvent.start)
        );
        const firstEvent = sortedEvents[0]?.scrapEyesEvent ?? null;

        const period: LabellerPeriodWithExpandedEvents = {
          periodId: index,
          periodIds: [index],
          start: videoStart,
          end: fullWindowEnd,
          events: events.map((e) => e.scrapEyesEvent),
        };

        return {
          labellerDetails,
          periods: [period],
          firstEvent,
        };
      }
    );
  }, [originalEventsByLabeller, videoStart, fullWindowEnd]);

  return (
    <Box
      ref={zoomAreaRef}
      onMouseMove={handleZoomMouseMove}
      onMouseLeave={handleZoomMouseLeave}
      sx={{ width: "100%", py: 2 }}
    >
      <Stack spacing={3}>
        {/* Merged event timeline */}
        <Box>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              {t("currentMergedEvent")}
            </Typography>
          </Stack>
          <Box
            ref={containerRef}
            sx={{
              position: "relative",
              ...trackInsetSx,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${footageStartPercent}%`,
                width: `${footageEndPercent - footageStartPercent}%`,
                bgcolor: "grey.50",
                pointerEvents: "none",
                zIndex: 0,
              }}
            />
            {beforeBoundary ? (
              <PeriodBoundaryOverlay
                side="before"
                percent={beforeBoundary.percent}
                label={beforeBoundary.label}
              />
            ) : null}
            {afterBoundary ? (
              <PeriodBoundaryOverlay
                side="after"
                percent={afterBoundary.percent}
                label={afterBoundary.label}
              />
            ) : null}
            {visibleGhostEvents.length > 0 ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  zIndex: 3,
                }}
              >
                <EventTimelineTrack
                  events={visibleGhostEvents}
                  videoStart={visibleStart}
                  videoDuration={visibleDuration}
                  currentTime={visibleCurrentTime}
                  showPlayhead={false}
                  onEventClick={onGhostEventClick}
                  clickableEventIds={clickableGhostEventIds}
                  highlightColorsByEventId={highlightColorsByEventId}
                  overbarHeight={overbarHeight}
                />
              </Box>
            ) : null}
            <Box sx={{ position: "relative", zIndex: 2 }}>
              <EventTimelineTrack
                events={mergedEvents}
                videoStart={visibleStart}
                videoDuration={visibleDuration}
                currentTime={visibleCurrentTime}
                showPlayhead={playheadInWindow}
                onMouseDown={handleMouseDown}
                selectedEventId={
                  markMergedEventSelected ? mergedEvent?.id : undefined
                }
                highlightColorsByEventId={highlightColorsByEventId}
                overbarHeight={overbarHeight}
              />
            </Box>
            {onTimingChange && mergedEvent ? (
              <>
                {dragState && previewRightPercent > previewLeftPercent ? (
                  <Box
                    sx={{
                      position: "absolute",
                      left: `${previewLeftPercent}%`,
                      width: `${previewRightPercent - previewLeftPercent}%`,
                      top: 0,
                      bottom: 0,
                      bgcolor: "primary.main",
                      opacity: 0.1,
                      pointerEvents: "none",
                      zIndex: 25,
                    }}
                  />
                ) : null}
                {draggableEdges.map((edge) => (
                  <Box
                    key={edge}
                    onMouseDown={handleEdgeMouseDown(edge)}
                    sx={{
                      position: "absolute",
                      left: `${edge === "start" ? resolvedStartPercent : resolvedEndPercent}%`,
                      top: 0,
                      bottom: 0,
                      width: 8,
                      transform: "translateX(-50%)",
                      cursor: "ew-resize",
                      zIndex: 30,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover > div": { opacity: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 2,
                        height: "60%",
                        bgcolor: "primary.main",
                        opacity: dragState?.edge === edge ? 1 : 0.4,
                        borderRadius: 1,
                        transition: "opacity 0.15s",
                        pointerEvents: "none",
                      }}
                    />
                  </Box>
                ))}
              </>
            ) : null}
          </Box>
        </Box>

        <Divider />

        {/* Original events by labeller */}
        <Stack spacing={2}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "text.secondary" }}
          >
            {t("originalEvents")}
          </Typography>
          {labellerPeriods.map(({ labellerDetails, periods, firstEvent }) => (
            <LabellerTimelineRow
              key={labellerDetails.id}
              labellerDetails={labellerDetails}
              periods={periods}
              visibleStart={visibleStart}
              visibleDuration={visibleDuration}
              currentTime={visibleCurrentTime}
              showPlayhead={playheadInWindow}
              trackSx={trackInsetSx}
              endAdornment={
                firstEvent !== null ? (
                  <Stack direction="row" spacing={0.5}>
                    {onPickTiming !== undefined ? (
                      <Tooltip title={t("pickTiming")}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AccessTimeIcon fontSize="small" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onPickTiming(firstEvent);
                          }}
                          sx={{
                            minWidth: 0,
                            px: 1,
                            py: 0.5,
                            fontSize: "0.75rem",
                          }}
                        >
                          {t("pickTiming")}
                        </Button>
                      </Tooltip>
                    ) : null}
                    {onPickArea !== undefined ? (
                      <Tooltip title={t("pickArea")}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LocationOnIcon fontSize="small" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onPickArea(firstEvent);
                          }}
                          sx={{
                            minWidth: 0,
                            px: 1,
                            py: 0.5,
                            fontSize: "0.75rem",
                          }}
                        >
                          {t("pickArea")}
                        </Button>
                      </Tooltip>
                    ) : null}
                    {onPickMaterial !== undefined ? (
                      <Tooltip title={t("pickMaterial")}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CategoryIcon fontSize="small" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onPickMaterial(firstEvent);
                          }}
                          sx={{
                            minWidth: 0,
                            px: 1,
                            py: 0.5,
                            fontSize: "0.75rem",
                          }}
                        >
                          {t("pickMaterial")}
                        </Button>
                      </Tooltip>
                    ) : null}
                  </Stack>
                ) : undefined
              }
            />
          ))}
        </Stack>

        {zoomContext?.isZoomed ? (
          <TimelineZoomCaption
            visibleStart={visibleStart}
            visibleEnd={visibleEnd}
            timeFormat={zoomCaptionTimeFormat}
            zoomFactor={videoDuration / visibleDuration}
            onResetZoom={zoomContext.resetZoom}
          />
        ) : null}
      </Stack>
    </Box>
  );
};
